import {
  Button,
  createListCollection,
  Field as ChakraField,
  Fieldset,
  HStack,
  Input,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import ErrorsContainer from '@components/common/form/ErrorsContainer';
import CustomInputDateTime from '@components/common/inputs/CustomInputDateTime';
import CoordinatesBar from '@components/map/controls/CoordinatesBar';
import { DraggablePanel } from '@components/ui/draggable';
import { Form } from '@components/ui/Form';
import { InputNumber } from '@components/ui/input/InputNumber';
import { CustomSelect, Option } from '@components/ui/select/Select';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { selectCoordinatesUnit } from '@redux/settings/settings.selectors';
import { useCreateEventMutation, useUpdateEventMutation } from '@services/replay/event.api';
import { createErrorMessage } from '@utils/common.utils';
import { eventColor } from '@utils/replay/event.utils';
import { eventSchema } from '@validations/event.schema';
import DmsCoordinates from 'dms-conversion';
import { Field, Formik } from 'formik';
import { useState } from 'react';
import { FaCircle } from 'react-icons/fa';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { CoordinatesUnitEnum } from '@/types/commons/commons.types';
import { DateTimeEnum } from '@/types/dateTime.types';
import { PopupComponentProps, PopupTypeEnum } from '@/types/popup.types';
import { EventTypeEnum, ReplayEvent } from '@/types/replay/replay.types';

import PopupContainer from './common/PopupContainer';

export function EventFormPopup({
  mapConstraintsRef,
  top,
  left,
  data,
  onClose,
}: Readonly<PopupComponentProps<PopupTypeEnum.EVENT>>) {
  const { newEvent, editedEvent } = data;
  const validationSchema = useValidation(eventSchema);
  const userSettingUnit = useAppSelector(selectCoordinatesUnit);
  const activeSite = useAppSelector(selectActiveSite);
  const [unit, setUnit] = useState<CoordinatesUnitEnum>(userSettingUnit);
  const dmsCoordinates = new DmsCoordinates(
    (editedEvent?.position ?? newEvent!.coordinates)?.latitude ?? 0,
    (editedEvent?.position ?? newEvent!.coordinates)?.longitude ?? 0,
  );
  const [eventCreation, { isLoading: isCreationLoading, isError: isCreationError, error: creationError }] =
    useCreateEventMutation();
  const [eventUpdate, { isLoading: isUpdateLoading, isError: isUpdateError, error: updateError }] =
    useUpdateEventMutation();
  const { formatMessage } = useIntl();

  const isLoading = isCreationLoading || isUpdateLoading;
  const isError = isCreationError || isUpdateError;
  const error = creationError ?? updateError;

  const initialValues: Partial<ReplayEvent> = {
    name: editedEvent?.name ?? '',
    code: editedEvent?.code ?? uuidv4(),
    startDate: editedEvent?.startDate ?? newEvent!.date,
    duration: editedEvent?.duration ?? 5,
    observationType: editedEvent?.observationType ?? undefined,
    position: { latitude: dmsCoordinates.latitude.dd, longitude: dmsCoordinates.longitude.dd, type: '2D' },
    comment: editedEvent?.comment ?? '',
    site: editedEvent?.site ?? activeSite?.code,
  };

  const onSubmit = (values: Partial<ReplayEvent>) => {
    (editedEvent ? eventUpdate({ id: editedEvent.id, ...values } as ReplayEvent) : eventCreation(values as ReplayEvent))
      .unwrap()
      .then(() => {
        createToast(
          formatMessage({ id: 'live.event.successToast' }, { creation: !editedEvent }),
          ToastStatusEnum.SUCCESS,
        );
        onClose();
      });
  };

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="live.event.formPopupTitle" values={{ creation: !editedEvent }} />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '420px', '4k': '480px' }}>
        <VStack backgroundColor="neutral.700" width="100%" gap={0.25}>
          {editedEvent && !!editedEvent.modificationTime && (
            <Text color="neutral.500" marginTop={0.5}>
              <FormattedMessage id="live.event.alreadyModified" />
            </Text>
          )}
          <Formik initialValues={{ ...initialValues }} onSubmit={onSubmit} validationSchema={validationSchema}>
            {({
              handleSubmit,
              dirty,
              isValid,
              errors,
              touched,
              handleChange,
              setFieldValue,
              initialValues,
              values,
            }) => (
              <Fieldset.Root
                invalid={(!!errors.name && touched.name) || (!!errors.observationType && touched.observationType)}
              >
                <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <Fieldset.Content
                    width="100%"
                    backgroundColor="neutral.850"
                    flexGrow={1}
                    alignItems="start"
                    gap={0.25}
                  >
                    <PopupContainer>
                      <VStack gap="standardS" width="100%">
                        <ChakraField.Root required invalid={touched.name && !!errors.name}>
                          <ChakraField.Label>
                            <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                              <FormattedMessage id="live.event.name" />
                            </Text>
                          </ChakraField.Label>
                          <Field
                            as={Input}
                            id="name"
                            name="name"
                            type="text"
                            height="48px"
                            maxLength="50"
                            invalid={touched.name && !!errors.name}
                          />
                        </ChakraField.Root>
                        <ChakraField.Root
                          id="observationType"
                          required
                          invalid={touched.observationType && !!errors.observationType}
                        >
                          <ChakraField.Label>
                            <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                              <FormattedMessage id="live.event.type" />
                            </Text>
                          </ChakraField.Label>
                          <CustomSelect
                            width="100%"
                            collection={createListCollection({
                              items: Object.entries(EventTypeEnum).map(([key, value]) => ({
                                label: formatMessage({ id: `live.event.types.${key}` }),
                                value,
                              })),
                            })}
                            formatLabel={(option: Option) => (
                              <HStack gap={1}>
                                <FaCircle color={eventColor[option.value as EventTypeEnum]} />
                                <Text textStyle="md" color="neutral.white">
                                  {option.label}
                                </Text>
                              </HStack>
                            )}
                            value={values.observationType}
                            onChange={(value) => value && handleChange('observationType')(value)}
                            defaultValue={initialValues.observationType}
                          />
                        </ChakraField.Root>
                      </VStack>
                    </PopupContainer>
                    <PopupContainer>
                      <VStack gap="standardS" width="100%">
                        <ChakraField.Root id="startDate" invalid={touched.startDate && !!errors.startDate}>
                          <ChakraField.Label>
                            <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                              <FormattedMessage id="live.event.dateTime" />
                            </Text>
                          </ChakraField.Label>
                          <Field
                            as={CustomInputDateTime}
                            id="startDate"
                            name="startDate"
                            type={DateTimeEnum.DATE_TIME}
                            borderRadius={0}
                            textStyle="screenStd"
                            showSecond={false}
                            isInvalid={touched.startDate && !!errors.startDate}
                            onChange={handleChange('startDate')}
                          />
                        </ChakraField.Root>
                        <ChakraField.Root id="duration" invalid={touched.duration && !!errors.duration}>
                          <ChakraField.Label>
                            <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                              <FormattedMessage id="live.event.duration" />
                            </Text>
                          </ChakraField.Label>
                          <Field
                            as={InputNumber}
                            id="duration"
                            name="duration"
                            max={120}
                            min={1}
                            onChange={(value?: number) => setFieldValue('duration', value)}
                            isInvalid={touched.duration && !!errors.duration}
                            suffix="min (max 120)"
                          />
                        </ChakraField.Root>
                      </VStack>
                    </PopupContainer>
                    <PopupContainer>
                      <ChakraField.Root>
                        <ChakraField.Label>
                          <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                            <FormattedMessage id="live.event.location" />
                          </Text>
                        </ChakraField.Label>
                        <CoordinatesBar
                          coordinatesDMS={dmsCoordinates}
                          backgroundColor="neutral.700"
                          handleUnitChange={(value) => setUnit(value as CoordinatesUnitEnum)}
                          unit={unit}
                        />
                      </ChakraField.Root>
                    </PopupContainer>
                    <PopupContainer>
                      <ChakraField.Root>
                        <ChakraField.Label>
                          <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                            <FormattedMessage id="live.event.comment" />
                          </Text>
                        </ChakraField.Label>
                        <Field
                          as={Textarea}
                          id="comment"
                          size="sm"
                          textStyle="screenSm"
                          name="comment"
                          width="100%"
                          backgroundColor="neutral.800"
                          height="80px"
                          resize="none"
                          overflowY="auto"
                          maxLength="500"
                        />
                      </ChakraField.Root>
                    </PopupContainer>
                    {(Object.keys(errors).some((key) => touched[key as keyof typeof touched]) || isError) && (
                      <PopupContainer>
                        <Fieldset.ErrorText width="100%">
                          <ErrorsContainer
                            errors={errors}
                            touched={touched}
                            apiError={isError ? createErrorMessage(error) : undefined}
                          />
                        </Fieldset.ErrorText>
                      </PopupContainer>
                    )}
                    <PopupContainer>
                      <HStack justify="space-around" width="100%">
                        <Button variant="secondary" type="button" onClick={onClose} flex={1}>
                          <FormattedMessage id="global.cancel" />
                        </Button>
                        <Button type="submit" disabled={!dirty || !isValid || isLoading} loading={isLoading} flex={1}>
                          <FormattedMessage id="live.event.createOrUpdate" values={{ creation: !editedEvent }} />
                        </Button>
                      </HStack>
                    </PopupContainer>
                  </Fieldset.Content>
                </Form>
              </Fieldset.Root>
            )}
          </Formik>
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
